/**
 * CustomerOrgsRepository – lecture customerOrgs (source de vérité clients par orga).
 * Zéro lecture orders : liste et fiche client utilisent uniquement ces données.
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    getCountFromServer,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    Timestamp,
    type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

const COLLECTION = 'customerOrgs';

function docId(orgId: string, customerId: string): string {
    return `${orgId}_${customerId}`;
}

export interface CustomerOrgView {
    id: string;
    orgId: string;
    customerId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    fullName: string;
    phone?: string;
    tiktokUsername?: string;
    firstPaidAt: Date | null;
    lastPaidAt: Date | null;
    ordersPaidCount: number;
    totalSpentCents: number;
    refundsCount: number;
    refundsCents: number;
    purchaseDaysCount: number;
    sumDaysBetweenPurchaseDays: number;
    avgDaysBetweenPurchaseDays: number;
}

function toDate(v: unknown): Date | null {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
        return (v as { toDate: () => Date }).toDate();
    }
    if (typeof v === 'string' || typeof v === 'number') return new Date(v);
    return null;
}

/** Lit les stats depuis l'objet `stats` du doc. */
function readStats(d: Record<string, unknown>) {
    const s = (d.stats ?? {}) as Record<string, unknown>;
    const n = (key: string, def: number) => (s[key] as number) ?? def;
    const dt = (key: string) => toDate(s[key]);
    return {
        firstPaidAt: dt('firstPaidAt'),
        lastPaidAt: dt('lastPaidAt'),
        ordersPaidCount: n('ordersPaidCount', 0),
        totalSpentCents: n('totalSpentCents', 0),
        refundsCount: n('refundsCount', 0),
        refundsCents: n('refundsCents', 0),
        purchaseDaysCount: n('purchaseDaysCount', 0),
        sumDaysBetweenPurchaseDays: n('sumDaysBetweenPurchaseDays', 0),
        avgDaysBetweenPurchaseDays: n('avgDaysBetweenPurchaseDays', 0),
    };
}

function fromFirestore(docSnap: DocumentSnapshot): CustomerOrgView {
    const d = docSnap.data() ?? {};
    const id = docSnap.id;
    const fn = (d.firstName as string) || '';
    const ln = (d.lastName as string) || '';
    const fullName = [fn, ln].filter(Boolean).join(' ').trim() || (d.email as string) || id;
    const st = readStats(d);
    return {
        id,
        orgId: (d.orgId as string) ?? '',
        customerId: (d.customerId as string) ?? '',
        email: (d.email as string) ?? '',
        firstName: (d.firstName as string) || undefined,
        lastName: (d.lastName as string) || undefined,
        fullName,
        phone: (d.phone as string) || undefined,
        tiktokUsername: (d.tiktokUsername as string) || undefined,
        firstPaidAt: st.firstPaidAt,
        lastPaidAt: st.lastPaidAt,
        ordersPaidCount: st.ordersPaidCount,
        totalSpentCents: st.totalSpentCents,
        refundsCount: st.refundsCount,
        refundsCents: st.refundsCents,
        purchaseDaysCount: st.purchaseDaysCount,
        sumDaysBetweenPurchaseDays: st.sumDaysBetweenPurchaseDays,
        avgDaysBetweenPurchaseDays: st.avgDaysBetweenPurchaseDays,
    };
}

export type CustomerOrgsSortField = 'lastPaidAt' | 'totalSpentCents';
export type CustomerOrgsFilter = 'all' | 'inactive';

export interface ListCustomerOrgsParams {
    orgId: string;
    sort: CustomerOrgsSortField;
    direction: 'asc' | 'desc';
    filter: CustomerOrgsFilter;
    /** Pour filter === 'inactive' : lastPaidAt < inactiveBefore */
    inactiveBefore?: Date;
    pageLimit: number;
    startAfterDoc?: DocumentSnapshot;
}

export interface ListCustomerOrgsResult {
    items: CustomerOrgView[];
    lastDoc: DocumentSnapshot | null;
    hasMore: boolean;
}

const SORT_TO_FIELD: Record<CustomerOrgsSortField, string> = {
    lastPaidAt: 'stats.lastPaidAt',
    totalSpentCents: 'stats.totalSpentCents',
};

/**
 * Liste paginée des clients d'une orga.
 * Tri : lastPaidAt ou totalSpentCents (champs sous `stats`).
 * Filtre : all | inactive (stats.lastPaidAt < inactiveBefore).
 * Si filter === 'inactive', le tri est forcé sur lastPaidAt (index).
 */
export async function listCustomerOrgs(params: ListCustomerOrgsParams): Promise<ListCustomerOrgsResult> {
    const { orgId, sort, direction, filter, inactiveBefore, pageLimit, startAfterDoc } = params;
    const coll = collection(db, COLLECTION);
    const sortField = filter === 'inactive' ? 'stats.lastPaidAt' : SORT_TO_FIELD[sort];
    let inactiveTs: Timestamp | null = null;
    if (inactiveBefore != null) {
        inactiveTs = inactiveBefore instanceof Timestamp ? inactiveBefore : Timestamp.fromDate(inactiveBefore);
    }

    const constraints: unknown[] = [
        where('orgId', '==', orgId),
        ...(filter === 'inactive' && inactiveTs ? [where('stats.lastPaidAt', '<', inactiveTs)] : []),
        orderBy(sortField, direction),
        limit(pageLimit),
        ...(startAfterDoc ? [startAfter(startAfterDoc)] : []),
    ];

    const q = query(coll, ...constraints);
    const snap = await getDocs(q);
    const items = snap.docs.map((d) => fromFirestore(d));
    const last = snap.docs.at(-1) ?? null;
    const hasMore = snap.docs.length === pageLimit;

    return { items, lastDoc: last, hasMore };
}

/**
 * Récupère un client orga par email.
 */
export async function getCustomerOrgByEmail(orgId: string, email: string): Promise<CustomerOrgView | null> {
    const coll = collection(db, COLLECTION);
    const q = query(
        coll,
        where('orgId', '==', orgId),
        where('email', '==', email),
        limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return fromFirestore(snap.docs[0]);
}

/**
 * Récupère un client orga par orgId + customerId.
 */
export async function getCustomerOrg(orgId: string, customerId: string): Promise<CustomerOrgView | null> {
    const ref = doc(db, COLLECTION, docId(orgId, customerId));
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return fromFirestore(snap);
}

/**
 * Nombre total de clients pour une orga.
 */
export async function countCustomerOrgs(orgId: string): Promise<number> {
    const coll = collection(db, COLLECTION);
    const q = query(coll, where('orgId', '==', orgId));
    const snap = await getCountFromServer(q);
    return snap.data().count;
}

/**
 * Nombre de clients inactifs (stats.lastPaidAt < before).
 */
export async function countInactiveCustomerOrgs(orgId: string, before: Date): Promise<number> {
    const coll = collection(db, COLLECTION);
    const ts = before instanceof Timestamp ? before : Timestamp.fromDate(before);
    const q = query(
        coll,
        where('orgId', '==', orgId),
        where('stats.lastPaidAt', '<', ts)
    );
    const snap = await getCountFromServer(q);
    return snap.data().count;
}
